// frontend/src/components/ProviderServices/ServiceFilters.tsx
// Mobile-first filters component

import React from 'react';
import { X } from 'lucide-react';
import { ProviderServiceFilters, ServiceFilterOptions } from '@/lib/api/providerServicesService';

interface ServiceFiltersProps {
  filters: ProviderServiceFilters;
  filterOptions?: ServiceFilterOptions;
  onFilterChange: (filters: Partial<ProviderServiceFilters>) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

const ServiceFilters: React.FC<ServiceFiltersProps> = ({
  filters,
  filterOptions,
  onFilterChange,
  onClearFilters,
  hasActiveFilters
}) => {
  const categories = filterOptions?.categories || [];
  const serviceTypes = filterOptions?.service_types || [];
  const providers = filterOptions?.providers || [];

  // Filter service types based on selected category
  const filteredServiceTypes = filters.category_id 
    ? serviceTypes.filter(st => st.category_id === filters.category_id)
    : serviceTypes;

  return (
    <div className="p-4 bg-white rounded-lg shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <X size={14} />
            Clear All
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Category Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <select
            value={filters.category_id || ''}
            onChange={(e) => onFilterChange({
              category_id: e.target.value ? parseInt(e.target.value, 10) : undefined,
              service_type_id: undefined // Reset service type when category changes
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Service Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Service Type
          </label>
          <select
            value={filters.service_type_id || ''}
            onChange={(e) => onFilterChange({
              service_type_id: e.target.value ? parseInt(e.target.value, 10) : undefined
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!filters.category_id && serviceTypes.length > 20} // Disable if too many options without category filter
          >
            <option value="">All Service Types</option>
            {filteredServiceTypes.map((serviceType) => (
              <option key={serviceType.id} value={serviceType.id}>
                {serviceType.name}
              </option>
            ))}
          </select>
          {!filters.category_id && serviceTypes.length > 20 && (
            <p className="mt-1 text-xs text-gray-500">
              Select a category first to filter service types
            </p>
          )}
        </div>

        {/* Status Filters - Mobile Optimized */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Active Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filters.is_active === undefined ? '' : filters.is_active.toString()}
              onChange={(e) => onFilterChange({
                is_active: e.target.value === '' ? undefined : e.target.value === 'true'
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="true">Active Only</option>
              <option value="false">Inactive Only</option>
            </select>
          </div>

          {/* Featured Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured
            </label>
            <select
              value={filters.is_featured === undefined ? '' : filters.is_featured.toString()}
              onChange={(e) => onFilterChange({
                is_featured: e.target.value === '' ? undefined : e.target.value === 'true'
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Services</option>
              <option value="true">Featured Only</option>
              <option value="false">Not Featured</option>
            </select>
          </div>
        </div>

        {/* Provider Filter (Admin Only) */}
        {providers.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Provider
            </label>
            <select
              value={filters.provider_id || ''}
              onChange={(e) => onFilterChange({
                provider_id: e.target.value ? parseInt(e.target.value, 10) : undefined
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Providers</option>
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Active Filter Summary */}
        {hasActiveFilters && (
          <div className="pt-4 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              {filters.category_id && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  Category: {categories.find(c => c.id === filters.category_id)?.name}
                  <button
                    onClick={() => onFilterChange({ category_id: undefined, service_type_id: undefined })}
                    className="hover:bg-blue-200 rounded-full p-0.5"
                  >
                    <X size={10} />
                  </button>
                </span>
              )}
              
              {filters.service_type_id && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  Type: {serviceTypes.find(st => st.id === filters.service_type_id)?.name}
                  <button
                    onClick={() => onFilterChange({ service_type_id: undefined })}
                    className="hover:bg-blue-200 rounded-full p-0.5"
                  >
                    <X size={10} />
                  </button>
                </span>
              )}
              
              {filters.is_active !== undefined && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  {filters.is_active ? 'Active' : 'Inactive'}
                  <button
                    onClick={() => onFilterChange({ is_active: undefined })}
                    className="hover:bg-green-200 rounded-full p-0.5"
                  >
                    <X size={10} />
                  </button>
                </span>
              )}
              
              {filters.is_featured !== undefined && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full">
                  {filters.is_featured ? 'Featured' : 'Not Featured'}
                  <button
                    onClick={() => onFilterChange({ is_featured: undefined })}
                    className="hover:bg-amber-200 rounded-full p-0.5"
                  >
                    <X size={10} />
                  </button>
                </span>
              )}
              
              {filters.provider_id && providers.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                  Provider: {providers.find(p => p.id === filters.provider_id)?.name}
                  <button
                    onClick={() => onFilterChange({ provider_id: undefined })}
                    className="hover:bg-purple-200 rounded-full p-0.5"
                  >
                    <X size={10} />
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceFilters;